import {
    AggregatorClient,
    AggregatorV2Client,
} from '@core/api/aggregator-client'
import { AssetsClient } from '@core/api/assets-client'
import { BaseService } from '@core/base/service/base-service'
import {
    transformAssets,
    transformUsers,
} from '@core/services/api-gateway/assets-service/transformers'

export const AssetsService = new (class AssetsService extends BaseService {
    private readonly client = AssetsClient
    private INTERVAL = import.meta.env.DEV ? 1000 : 1000 * 5

    constructor() {
        super('assets.service')
    }

    async getSecurityAssessment() {
        const [error, result] = await this.request(
            () => this.client.getAccountSecurityAssessment(this.accountId),
            {
                poll: {
                    maxRetries: 1000,
                    delay: this.INTERVAL,
                    shouldStop: (response) => {
                        return ![202, 204].includes(response?.status || 0)
                    },
                },
            }
        )

        if (error) {
            console.log('error Security Assessment', error)
            return undefined
        }
        const { security_score: securityScore } = result?.data?.data as {
            [key: string]: unknown
        }
        return {
            securityScore,
        } as {
            securityScore: number | null
        }
    }

    async getIssues(polling: boolean = true) {
        const [error, result] = await this.request(
            () => this.client.getAccountIssues(this.accountId),
            {
                poll: {
                    maxRetries: 1000,
                    delay: this.INTERVAL,
                    shouldStop: (response) => {
                        return (
                            !polling ||
                            ![202, 204].includes(response?.status || 0)
                        )
                    },
                },
            }
        )

        if (error) {
            console.log('error', error)
            return []
        }
        return result?.data?.data?.issues || []
    }

    async getIssuesByType(polling: boolean = true): Promise<TIssue[]> {
        const [error, result] = await this.request(
            () => AggregatorV2Client.getAccountIssuesByTypes(this.accountId),
            {
                poll: {
                    maxRetries: 1000,
                    delay: this.INTERVAL,
                    shouldStop: (response) => {
                        return (
                            !polling ||
                            ![202, 204].includes(response?.status || 0)
                        )
                    },
                },
            }
        )
        if (error) {
            console.log('error', error)
            return []
        }

        return (result?.data?.data?.account_issues
            ?.filter(
                (accountIssue) =>
                    accountIssue.type === 'CLOUD_APP' ||
                    accountIssue.type === 'USER_LEAKED_CREDENTIALS' ||
                    accountIssue.type === 'DEVICE_INFOSTEALER'
            )
            .flatMap((accountIssue) => accountIssue.issues ?? []) ??
            []) as unknown as TIssue[]
    }

    async getAssets() {
        const [error, result] = await this.request(
            async () => {
                const assetsResult = await this.client.getAccountAssets(
                    this.accountId
                )

                const deviceResult = await this.http.get(
                    `/api/accounts/${this.accountId}/devices`
                )

                const userResult = await AggregatorClient.getAccountUsers(
                    this.accountId
                )
                // TODO: use SDK when ready
                const cloudAppResult = await this.http.get(
                    `/api/accounts/${this.accountId}/applications`
                )

                return [assetsResult, deviceResult, userResult, cloudAppResult]
            },
            {
                poll: {
                    maxRetries: 1000,
                    delay: this.INTERVAL,
                    shouldStop: (response) =>
                        // @ts-ignore
                        ![202, 204].includes(response?.status || 0),
                },
            }
        )

        if (error) {
            console.log('assets error', error.message)
            return []
        }
        // @ts-ignore
        const [assetsResult, deviceResult, userResult, cloudAppResult] = result

        const assets = assetsResult?.data?.data?.assets || []
        const devices = deviceResult?.data?.data?.devices || []
        const users = transformUsers(userResult?.data?.data?.users)
        const cloudApps = cloudAppResult?.data?.data?.applications || []
        return transformAssets([...assets, ...devices, ...users, ...cloudApps])
    }

    async getTechnologies() {
        const [error, result] = await this.request(
            () => this.client.getAccountTechnologies(this.accountId),
            {
                poll: {
                    maxRetries: 1000,
                    delay: this.INTERVAL,
                    shouldStop: (response) => {
                        return ![202, 204].includes(response?.status || 0)
                    },
                },
            }
        )
        if (error) {
            console.log('error', error)
            return []
        }
        return result?.data?.data?.technologies || []
    }
})()
